import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, Tag as TagIcon, ChevronLeft, Share2, Twitter, Facebook, MessageCircle, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Footer from '../components/sections/Footer';
import SEO from '../components/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../seo/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Add data-testid + ids to all H2/H3 in body HTML so TOC links work
const enrichBodyHtml = (html) => {
  if (!html) return { html: '', toc: [] };
  // Fix relative upload URLs (TinyMCE saves `api/uploads/x.png` without a leading slash,
  // which breaks on detail pages like `/blog/<slug>` resolving to `/blog/api/...`).
  const fixed = html.replace(/(src|href)=(["'])(api\/uploads\/)/g, '$1=$2/$3');
  const toc = [];
  const slugify = (text) => (text || '').toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || `h-${Math.random().toString(36).slice(2, 8)}`;
  const enriched = fixed.replace(/<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi, (_m, tag, attrs, inner) => {
    const text = inner.replace(/<[^>]+>/g, '').trim();
    if (!text) return _m;
    const id = slugify(text);
    toc.push({ id, text, level: tag === 'h2' ? 2 : 3 });
    if (/id\s*=\s*"/i.test(attrs)) return _m;
    return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
  });
  return { html: enriched, toc };
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isZh = i18n.language === 'zh';
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setNotFound(false);
      try {
        const r = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(r.data);
        try {
          const rel = await axios.get(`${API}/blog/posts/${slug}/related?limit=3`);
          setRelated(rel.data?.items || []);
        } catch { setRelated([]); }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally { setLoading(false); }
    };
    load();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const title = post ? (isZh ? (post.title_zh || post.title_en) : (post.title_en || post.title_zh)) : '';
  const excerpt = post ? (isZh ? (post.excerpt_zh || post.excerpt_en) : (post.excerpt_en || post.excerpt_zh)) : '';
  const bodyRaw = post ? (isZh ? (post.body_html_zh || post.body_html_en) : (post.body_html_en || post.body_html_zh)) : '';

  const { html: bodyHtml, toc } = useMemo(() => enrichBodyHtml(bodyRaw), [bodyRaw]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return ''; }
  };

  const canonical = `https://www.aligcus.com${isZh ? '/zh' : ''}/blog/${slug}`;
  const seoTitle = post ? (isZh ? (post.meta_title_zh || post.title_zh) : (post.meta_title_en || post.title_en)) : '';
  const seoDesc = post ? (isZh ? (post.meta_description_zh || post.excerpt_zh) : (post.meta_description_en || post.excerpt_en)) : '';

  const articleSchema = post ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: excerpt,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { '@type': 'Person', name: post.author_name || 'ALIGCUS Team' },
    publisher: {
      '@type': 'Organization',
      name: 'ALIGCUS Construction Inc',
      logo: { '@type': 'ImageObject', url: 'https://www.aligcus.com/api/uploads/f823eb4b3952.jpg' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    keywords: (post.tags || []).join(', '),
    inLanguage: isZh ? 'zh-CN' : 'en-US',
    wordCount: (bodyRaw || '').replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length,
    timeRequired: post.read_time_min ? `PT${post.read_time_min}M` : undefined,
  } : null;

  const shareUrl = canonical;
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => toast.success(isZh ? '链接已复制' : 'Link copied'));
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-16 text-center">
          <div>
            <h1 className="text-3xl font-bold mb-3">{isZh ? '文章未找到' : 'Article Not Found'}</h1>
            <p className="text-gray-600 mb-6">{isZh ? '此文章可能已被删除或链接有误。' : 'This article may have been removed or the link is incorrect.'}</p>
            <Button onClick={() => navigate(isZh ? '/zh/blog' : '/blog')} className="bg-red-600 hover:bg-red-700"><ChevronLeft className="w-4 h-4 mr-1" />{isZh ? '回到博客' : 'Back to Blog'}</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading || !post) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">{isZh ? '加载中...' : 'Loading...'}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={canonical}
        image={post.cover_image || undefined}
        lang={isZh ? 'zh' : 'en'}
        alternates={[
          { lang: 'en', url: `https://www.aligcus.com/blog/${slug}` },
          { lang: 'zh-CN', url: `https://www.aligcus.com/zh/blog/${slug}` },
        ]}
        schemas={[
          getOrganizationSchema(isZh ? 'zh' : 'en'),
          getBreadcrumbSchema([
            { name: isZh ? '首页' : 'Home', url: isZh ? 'https://www.aligcus.com/zh/' : 'https://www.aligcus.com/' },
            { name: isZh ? '博客' : 'Blog', url: isZh ? 'https://www.aligcus.com/zh/blog' : 'https://www.aligcus.com/blog' },
            { name: title, url: canonical },
          ]),
          articleSchema,
        ]}
      />

      {/* Hero */}
      <section className="bg-gray-900 text-white py-16 pt-28" data-testid="blog-post-hero">
        <div className="max-w-4xl mx-auto px-6">
          <Link to={isZh ? '/zh/blog' : '/blog'} className="inline-flex items-center text-gray-400 hover:text-white mb-6 text-sm"><ChevronLeft className="w-4 h-4 mr-1" />{isZh ? '所有文章' : 'All articles'}</Link>
          {(post.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map(t => (
                <Link key={t} to={`${isZh ? '/zh/blog' : '/blog'}?tag=${encodeURIComponent(t)}`} className="text-xs bg-red-600/20 text-red-300 px-3 py-1 rounded-full hover:bg-red-600/30">#{t}</Link>
              ))}
            </div>
          )}
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight mb-4">{title}</h1>
          {excerpt && <p className="text-lg text-gray-300 mb-6">{excerpt}</p>}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              {post.author_avatar ? <img src={post.author_avatar} alt={post.author_name} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold">{(post.author_name || 'A')[0]}</div>}
              <span>{post.author_name}</span>
            </div>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{fmtDate(post.published_at)}</span>
            {post.read_time_min ? <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{post.read_time_min} {isZh ? '分钟阅读' : 'min read'}</span> : null}
          </div>
        </div>
      </section>

      {/* Cover */}
      {post.cover_image && (
        <div className="max-w-4xl mx-auto px-6 -mt-10">
          <img src={post.cover_image} alt={title} className="w-full rounded-xl shadow-xl object-cover aspect-[16/9]" />
        </div>
      )}

      {/* Body + TOC */}
      <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_220px] gap-10">
        <article className="prose prose-lg max-w-none prose-headings:scroll-mt-28 prose-img:rounded-lg" data-testid="blog-post-body">
          <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />

          {/* Share */}
          <div className="not-prose border-t border-gray-200 mt-12 pt-6 flex items-center gap-3 flex-wrap">
            <span className="text-sm text-gray-600 flex items-center gap-1"><Share2 className="w-4 h-4" />{isZh ? '分享' : 'Share'}:</span>
            <a target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" data-testid="share-twitter"><Twitter className="w-4 h-4" /></a>
            <a target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" data-testid="share-facebook"><Facebook className="w-4 h-4" /></a>
            <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(title + ' ' + shareUrl)}`} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" data-testid="share-whatsapp"><MessageCircle className="w-4 h-4" /></a>
            <button onClick={copyLink} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200" data-testid="share-copy"><LinkIcon className="w-4 h-4" /></button>
          </div>
        </article>

        {/* TOC sidebar (desktop) */}
        {toc.length > 1 && (
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">{isZh ? '目录' : 'Contents'}</div>
              <ul className="space-y-2 text-sm border-l border-gray-200" data-testid="blog-post-toc">
                {toc.map((it, i) => (
                  <li key={i} className={it.level === 3 ? 'pl-6' : 'pl-3'}>
                    <a href={`#${it.id}`} className="text-gray-700 hover:text-red-600 block py-1 -ml-px border-l-2 border-transparent hover:border-red-500 pl-3">{it.text}</a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold mb-6">{isZh ? '相关文章' : 'Related Articles'}</h2>
            <div className="grid md:grid-cols-3 gap-6" data-testid="blog-related">
              {related.map(r => {
                const rTitle = isZh ? (r.title_zh || r.title_en) : (r.title_en || r.title_zh);
                const rLink = `${isZh ? '/zh/blog' : '/blog'}/${r.slug}`;
                return (
                  <Link key={r.id} to={rLink} className="bg-white rounded-lg shadow-sm hover:shadow-lg transition overflow-hidden">
                    {r.cover_image && <img src={r.cover_image} alt={rTitle} className="w-full aspect-[16/10] object-cover" loading="lazy" />}
                    <div className="p-4">
                      <h3 className="font-semibold text-base line-clamp-2 hover:text-red-600 transition">{rTitle}</h3>
                      <div className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(r.published_at)}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
